import { useEffect } from 'react'

import { format, isBefore } from 'date-fns'

import { useTravelDates } from './useTravelDates'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'

export function useSyncDatesFallback() {
  const { dispatch, state } = useTravelSearchContext()
  const { homeboundTravelDates, outboundTravelDates } = useTravelDates()

  const { homeboundDate, outboundDate } = state.selectedDates ?? {}

  useEffect(() => {
    if (
      outboundDate &&
      !outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd')) &&
      !isBefore(outboundDate, new Date(outboundTravelDates.dates.fromDate)) &&
      !isBefore(new Date(outboundTravelDates.dates.toDate), outboundDate)
    ) {
      return
    }

    if (
      outboundDate &&
      outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd')) &&
      outboundTravelDates.defaultDate &&
      !outboundTravelDates?.disabledDates.includes(outboundTravelDates.defaultDate)
    ) {
      const fallbackDate = new Date(outboundTravelDates.defaultDate)
      dispatch({
        dates: {
          homeboundDate,
          outboundDate: fallbackDate,
        },
        type: 'SET_DATES',
      })
      return
    }

    if (
      outboundDate &&
      isBefore(outboundDate, new Date(outboundTravelDates.dates.fromDate)) &&
      outboundTravelDates.defaultDate &&
      !outboundTravelDates?.disabledDates.includes(outboundTravelDates.defaultDate)
    ) {
      const fallbackDate = new Date(outboundTravelDates.defaultDate)
      dispatch({
        dates: {
          homeboundDate,
          outboundDate: fallbackDate,
        },
        type: 'SET_DATES',
      })
      return
    }

    if (
      outboundDate &&
      isBefore(new Date(outboundTravelDates.dates.toDate), outboundDate) &&
      outboundTravelDates.defaultDate &&
      !outboundTravelDates?.disabledDates.includes(outboundTravelDates.defaultDate)
    ) {
      const fallbackDate = new Date(outboundTravelDates.defaultDate)
      dispatch({
        dates: {
          homeboundDate,
          outboundDate: fallbackDate,
        },
        type: 'SET_DATES',
      })
      return
    }

    //prevent customer for selecting disabled date in outbound datepicker
    if (outboundDate && outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd'))) {
      const fallbackOutbound = getNextAvailableDate(
        format(outboundDate, 'yyyy-MM-dd'),
        outboundTravelDates.disabledDates,
        true,
        outboundTravelDates?.dates?.toDate
      )
      dispatch({
        dates: {
          homeboundDate,
          outboundDate: fallbackOutbound,
        },
        type: 'SET_DATES',
      })
      return
    }

    if (!outboundDate && outboundTravelDates?.defaultDate) {
      const fallbackDate = new Date(outboundTravelDates.defaultDate)

      dispatch({
        dates: {
          homeboundDate,
          outboundDate: fallbackDate,
        },
        type: 'SET_DATES',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboundTravelDates, outboundDate, state.isDayTrip, dispatch])

  useEffect(() => {
    if (state.isOneWay) return

    if (state.isDayTrip && outboundDate && homeboundTravelDates) {
      // For day trips, use the outbound date range (not homebound range) for validation
      const dateRange = outboundTravelDates || homeboundTravelDates
      const currentDateStr = format(outboundDate, 'yyyy-MM-dd')
      const isWithinRange = currentDateStr >= dateRange.dates.fromDate && currentDateStr <= dateRange.dates.toDate
      const isNotDisabled = !dateRange?.disabledDates.includes(currentDateStr)

      if (isWithinRange && isNotDisabled) {
        dispatch({
          dates: {
            homeboundDate: outboundDate,
            outboundDate,
          },
          type: 'SET_DATES',
        })
        return
      } else {
        // Current date is invalid - fallback to outbound default date (not homebound)
        const fallbackDate = new Date(dateRange.defaultDate)
        dispatch({
          dates: {
            homeboundDate: fallbackDate,
            outboundDate: fallbackDate,
          },
          type: 'SET_DATES',
        })
        return
      }
    }

    // Handle day trip case when homeboundTravelDates is not yet available
    if (state.isDayTrip && outboundDate && !homeboundTravelDates && outboundTravelDates) {
      // Check if the current date is within the outbound valid date range AND not disabled
      const currentDateStr = format(outboundDate, 'yyyy-MM-dd')
      const isWithinRange = currentDateStr >= outboundTravelDates.dates.fromDate && currentDateStr <= outboundTravelDates.dates.toDate
      const isNotDisabled = !outboundTravelDates?.disabledDates.includes(currentDateStr)

      if (!isWithinRange || !isNotDisabled) {
        // Current date is invalid - fallback to default date
        const fallbackDate = new Date(outboundTravelDates.defaultDate)
        dispatch({
          dates: {
            homeboundDate: fallbackDate,
            outboundDate: fallbackDate,
          },
          type: 'SET_DATES',
        })
        return
      }
    }

    if (outboundDate && outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd'))) {
      return
    }

    if (
      homeboundDate &&
      !homeboundTravelDates?.disabledDates.includes(format(homeboundDate, 'yyyy-MM-dd')) &&
      outboundDate &&
      homeboundDate >= outboundDate
    ) {
      return
    }

    //prevent customer for selecting disabled date in homebound datepicker
    if (homeboundDate && homeboundTravelDates?.disabledDates.includes(format(homeboundDate, 'yyyy-MM-dd'))) {
      const fallbackHomebound = getNextAvailableDate(
        homeboundTravelDates.defaultDate,
        homeboundTravelDates.disabledDates,
        false,
        homeboundTravelDates?.dates?.toDate
      )
      dispatch({
        dates: {
          homeboundDate: fallbackHomebound,
          outboundDate,
        },
        type: 'SET_DATES',
      })
      return
    }
    // Otherwise fallback to next available date if present
    else if (homeboundTravelDates && outboundDate) {
      const fallbackHomebound = getNextAvailableDate(
        format(outboundDate, 'yyyy-MM-dd'),
        homeboundTravelDates.disabledDates,
        false,
        homeboundTravelDates.dates.toDate,
        false
      )
      dispatch({
        dates: {
          homeboundDate: fallbackHomebound,
          outboundDate,
        },
        type: 'SET_DATES',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeboundTravelDates, homeboundDate, outboundDate, state.isOneWay, state.isDayTrip, dispatch])
}

function getNextAvailableDate(
  startDate: string,
  unavailableDates: string[],
  invers: boolean,
  endDate?: string,
  allowSameDate = true
): Date {
  const originalStart = new Date(startDate)

  const current = new Date(startDate)
  if (!invers && !allowSameDate) {
    current.setDate(current.getDate() + 1)
  }

  while (true) {
    const currentStr = format(current, 'yyyy-MM-dd')
    if (!unavailableDates.includes(currentStr)) {
      return current
    }
    if (endDate && currentStr === endDate) {
      return originalStart
    }
    current.setDate(current.getDate() + (invers ? -1 : 1))
  }
}
